import type { Values } from '@bayer-int/portletce-shared';
import type { Signal } from '@bayer-int/portletce-aclibv2';
import { mapValues, isEqual, omitBy, isUndefined } from 'lodash-es';
import * as t from 'io-ts';

import { concatUniqByProp } from 'portlet/common/utils';
import type { SettingFormInputDefinition } from 'header/types';

import { toFilterableSettings } from './filterableSettings';
import type {
    SettingDefinitionMap,
    SettingValueMap} from './settingDefinitions';
import {
    SettingSpecValueCodecs,
    SettingDefinition,
} from './settingDefinitions';
import { CustomSettingFormDefinitionInput } from './customSettingFormDefinitionInput';
import type { FilterData } from '../filters';
import type { FilterIdToSignalMapping } from '../listenTo';
import type { PortletQuery } from '../query';

export { SettingKeys, SettingDefinition, DRILL_BY_NONE_OPTION } from './settingDefinitions';
export type {
    SettingDefinitionMap,
    SettingDefinitionWithExtraInformation,
    SettingsSource,
    SettingValueMap,
    SettingPerViewDictionary,
} from './settingDefinitions';

/**
 * These settings are 'known', meaning that we (developers) expect them.
 *
 * Additionally, users can define their own settings, by providing full `SettingFormInputDefinition` (see CustomSettingsC).
 */
const KnownSettingsC = t.partial(
    {
        scale: SettingSpecValueCodecs.SCALE,
        scaleValues: SettingSpecValueCodecs.SCALE_VALUES,
        dateType: SettingSpecValueCodecs.DATE_TYPE,
        dateRange: SettingSpecValueCodecs.DATE_RANGE,
        title: SettingSpecValueCodecs.TITLE,
        subtitle: SettingSpecValueCodecs.SUBTITLE,
        titleAlign: SettingSpecValueCodecs.TITLE_ALIGN,
        internalKey: SettingSpecValueCodecs.INTERNAL_KEY,
        dimensionValues: SettingSpecValueCodecs.DIMENSION_VALUES,
        hierarchyValues: SettingSpecValueCodecs.HIERARCHY_VALUES,
        hierarchyId: SettingSpecValueCodecs.HIERARCHY_ID,
        hierarchyVariantId: SettingSpecValueCodecs.HIERARCHY_VARIANT_ID,
        linkedHierarchies: SettingSpecValueCodecs.LINKED_HIERARCHY_IDS,
        singleSelect: SettingSpecValueCodecs.SINGLE_SELECT,
        labelDisplayStyle: SettingSpecValueCodecs.LABEL_DISPLAY_STYLE,
        mandatorySettings: SettingSpecValueCodecs.MANDATORY_SETTINGS,
        resettableSettings: SettingSpecValueCodecs.RESETTABLE_SETTINGS,
        drill: SettingSpecValueCodecs.DRILL,
        condition: SettingSpecValueCodecs.CONDITION,
        smartView: SettingSpecValueCodecs.SMART_VIEW,
        smartViewValues: SettingSpecValueCodecs.SMART_VIEW_VALUES,
        shortLabels: SettingSpecValueCodecs.SHORT_LABELS,
        autoDate: SettingSpecValueCodecs.AUTO_DATE,
        allowClear: SettingSpecValueCodecs.ALLOW_CLEAR,
        sort: SettingSpecValueCodecs.SORT,
        inputBWQuery: SettingSpecValueCodecs.INPUT_BW_QUERY,
        role: SettingSpecValueCodecs.ROLE,
        BWsystem: SettingSpecValueCodecs.BW_SYSTEM,
        infoProvider: SettingSpecValueCodecs.INFO_PROVIDER,
        shortcuts: SettingSpecValueCodecs.SHORTCUTS,
        shortcutsColor: SettingSpecValueCodecs.SHORTCUTS_COLOR,
        viewForm: SettingSpecValueCodecs.VIEW_FORM,
        applyFilterOnSelection: SettingSpecValueCodecs.APPLY_FILTER_ON_SELECTION,
        highlightOnFilterInCurrentDrill:
            SettingSpecValueCodecs.HIGHLIGHT_ON_FILTER_IN_CURRENT_DRILL,
        emitFilterOnExactDrill: SettingSpecValueCodecs.APPLY_FILTER_ON_EXACT_DRILL_ONLY,
        applyFilterToUpstreamHierarchyLevels:
            SettingSpecValueCodecs.APPLY_FILTER_TO_UPSTREAM_HIERARCHY_LEVELS,
        dbxCondition: SettingSpecValueCodecs.DATABRICKS_CONDITION,
        dbxDrill: SettingSpecValueCodecs.DATABRICKS_DRILL,
    },
    'KnownSettings',
);

const CustomSettingsValueC = t.union(
    // cast required to assert that the array has at least two elements after spreading (required by t.union)
    // we have to union with `SettingSpecValueCodecs` because we intersect object with known keys and dynamic keys (of type string)
    // more info: https://stackoverflow.com/a/47594145
    //
    // for actual schema validation we want to get rid of that subsequent union elements (see _schemas.ts)
    [CustomSettingFormDefinitionInput.codec, ...Object.values(SettingSpecValueCodecs)] as [
        t.Type<SettingFormInputDefinition> | Values<typeof SettingSpecValueCodecs>,
        t.Type<SettingFormInputDefinition> | Values<typeof SettingSpecValueCodecs>,
    ],
    'CustomSettingsValue',
);

/** Users can define their own settings by providing setting definition. */
const CustomSettingsC = t.record(t.string, CustomSettingsValueC, 'CustomSettings');

/**
 * Defines available settings both on specification and view level. Includes both known and custom settings.
 */
const SettingsC = t.intersection([KnownSettingsC, CustomSettingsC], 'Settings');

export type Settings = t.TypeOf<typeof SettingsC>;

export type Setting = {
    key: keyof SettingValueMap;
    value: Values<SettingValueMap>;
};

export type SettingValueMapDiff = Partial<{
    [key in keyof SettingValueMap]: {
        current: SettingValueMap[key] | undefined;
        previous: SettingValueMap[key] | undefined;
    };
}>;

export const Settings = {
    codec: SettingsC,
    keyValueCodec: t.type({ key: t.string, value: t.unknown }) as t.Type<Setting>,
    knownSettingsCodec: KnownSettingsC,
    customSettingsCodec: CustomSettingsC,
    customSettingsValueCodec: CustomSettingsValueC,

    toFilterableSettings,
    mergeKeyValuePairs: concatUniqByProp('key'),

    toSignals: (
        settings: Partial<SettingValueMap>,
        settingDefinitions: Partial<SettingDefinitionMap>,
        mergedSettings: Partial<SettingValueMap>,
        currentViewSignals: Signal[],
        filters: FilterData[],
        queryMetadata: PortletQuery | null,
        filterToSignalName: FilterIdToSignalMapping,
    ): Signal[] =>
        SettingDefinition.toSignals(
            settings,
            settingDefinitions,
            mergedSettings,
            currentViewSignals,
            filters,
            queryMetadata,
            filterToSignalName,
        ),

    merge: (
        ...settingValues: Array<Partial<SettingValueMap> | undefined | null | false>
    ): Partial<SettingValueMap> => Object.assign({}, ...settingValues),

    mergeSpecSettings: (
        ...settings: Array<Partial<Settings> | undefined | null | false>
    ): Partial<Settings> => Object.assign({}, ...settings),

    updateSetting: (
        settings: Partial<SettingValueMap>,
        setting: Setting,
    ): Partial<SettingValueMap> => ({
        ...settings,
        [setting.key]: setting.value,
    }),

    diffSettings: (
        newSettingValues: Partial<SettingValueMap>,
        oldSettingValues: Partial<SettingValueMap>,
    ): SettingValueMapDiff => {
        const keys = Object.keys({ ...oldSettingValues, ...newSettingValues });

        return keys.reduce((settingDiff, key) => {
            const current = newSettingValues[key];
            const previous = oldSettingValues[key];

            return !isEqual(current, previous)
                ? { ...settingDiff, [key]: { current, previous } }
                : settingDiff;
        }, {});
    },

    /**
     * Merges .current values of SettingValueMapDiff with current settings.
     * If setting is not present in diff, it is preserved,
     * if setting is present in diff.current and its value is `undefined` it is
     * subtracted from `settings`. Otherwise the .current value is merged into `settings`.
     */

    applyDiff: (
        settings: Partial<SettingValueMap>,
        diff: SettingValueMapDiff,
    ): Partial<SettingValueMap> => {
        const settingsToSet = Object.entries(diff).reduce((acc, [key, value]) => {
            if (isUndefined(value) || isUndefined(value.current)) return acc;
            acc[key] = value.current;
            return acc;
        }, {} as Partial<SettingValueMap>);

        const settingsSansExcluded = omitBy(settings, (_, key) => {
            const settingDiff: Values<SettingValueMapDiff> = diff[key];

            return !isUndefined(settingDiff) && isUndefined(settingDiff.current);
        });

        return Settings.merge(settingsSansExcluded, settingsToSet);
    },

    fromSettingList: (settings: Setting[]): Partial<SettingValueMap> =>
        settings.reduce(Settings.updateSetting, {}),

    toSettingList: (settings: Partial<SettingValueMap>): Setting[] =>
        (Object.entries(settings) as Array<[keyof SettingValueMap, Values<SettingValueMap>]>).map(
            ([key, value]) => ({ key, value }),
        ),

    matchDefinitionsWithValues: (
        definitions: Partial<SettingDefinitionMap>,
        settings: Partial<SettingValueMap>,
    ): Partial<SettingDefinitionMap> =>
        mapValues(definitions, (definition, key) =>
            settings[key] !== undefined
                ? {
                      ...definition,
                      value: settings[key],
                  }
                : definition,
        ) as Partial<SettingDefinitionMap>,
};
